import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Building2, Mail, Lock, User, CheckCircle2, Navigation, RefreshCw, MapPin } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import RegionCascade from '../components/RegionCascade';

const inputCls = 'w-full pl-11 pr-4 py-3 rounded-2xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body text-sm';
const fieldCls = 'w-full px-4 py-3 rounded-2xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body text-sm';

const TakmirRegisterPage = () => {
  const navigate = useNavigate();
  const { registerTakmir } = useAuth();
  const [form, setForm] = useState({ name: '', email: '', password: '', mosque_name: '', village_id: '', address: '', phone: '', latitude: '', longitude: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);

  const handleGPS = () => {
    if (!navigator.geolocation) { setError('Browser tidak mendukung GPS'); return; }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm(f => ({
          ...f,
          latitude: parseFloat(pos.coords.latitude.toFixed(7)).toString(),
          longitude: parseFloat(pos.coords.longitude.toFixed(7)).toString(),
        }));
        setGpsLoading(false);
      },
      () => { setError('Gagal mendapatkan lokasi GPS. Silakan isi manual.'); setGpsLoading(false); },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.village_id) { setError('Pilih wilayah lengkap sampai Desa/Kelurahan.'); return; }
    setLoading(true);
    const payload = {
      ...form,
      latitude: form.latitude ? parseFloat(form.latitude) : null,
      longitude: form.longitude ? parseFloat(form.longitude) : null,
    };
    const res = await registerTakmir(payload);
    setLoading(false);
    if (res.success) setDone(true);
    else setError(res.error);
  };

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-emerald-100 flex items-center justify-center px-6" data-testid="takmir-register-success">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-3xl shadow-xl p-8 max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="w-9 h-9 text-emerald-600" /></div>
          <h1 className="text-2xl font-heading font-bold text-primary-dark mb-2">Pendaftaran Berhasil!</h1>
          <p className="text-sm font-body text-text-secondary mb-6">
            Masjid Anda telah didaftarkan dan sedang menunggu persetujuan Super Admin. Anda sudah bisa masuk ke panel untuk menyiapkan konten. Masjid akan tampil publik setelah disetujui.
          </p>
          <button data-testid="takmir-success-admin-btn" onClick={() => navigate('/admin')} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-body font-semibold py-3 rounded-full">Buka Panel Takmir</button>
        </motion.div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen bg-gradient-to-b from-emerald-50 to-emerald-100 flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-lg">
        <div className="bg-white rounded-3xl shadow-xl border border-stone-100 p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3"><Building2 className="w-8 h-8 text-emerald-700" /></div>
            <h1 className="text-3xl font-heading font-bold text-primary-dark mb-1">Daftarkan Masjid</h1>
            <p className="text-sm font-body text-text-secondary">Bergabung ke platform Masjid Pro sebagai Takmir</p>
          </div>

          {error && <div data-testid="register-error" className="bg-red-50 border border-red-200 rounded-2xl p-3 mb-4"><p className="text-sm font-body text-red-800">{error}</p></div>}

          <form onSubmit={submit} className="space-y-3">
            <input data-testid="register-mosque-name" className="w-full px-4 py-3 rounded-2xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body text-sm" placeholder="Nama Masjid" value={form.mosque_name} onChange={(e) => setForm({ ...form, mosque_name: e.target.value })} required />
            <RegionCascade onVillageChange={(vid) => setForm((f) => ({ ...f, village_id: vid }))} />
            <input className={fieldCls} placeholder="Alamat Masjid" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />

            {/* GPS Koordinat */}
            <div className="bg-stone-50 rounded-2xl p-4 space-y-3 border border-stone-100">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-stone-600 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-emerald-600" /> Koordinat GPS (opsional)
                </p>
                <button type="button" onClick={handleGPS} disabled={gpsLoading}
                  data-testid="takmir-gps-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-full text-xs font-medium disabled:opacity-60 transition-colors">
                  {gpsLoading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Navigation className="w-3 h-3" />}
                  {gpsLoading ? 'Mengambil...' : 'Ambil Lokasi Saya'}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-stone-500 mb-1">Latitude</label>
                  <input type="number" step="any" value={form.latitude} data-testid="takmir-latitude"
                    onChange={e => setForm(f => ({ ...f, latitude: e.target.value }))}
                    className={fieldCls} placeholder="1.000000" />
                </div>
                <div>
                  <label className="block text-xs text-stone-500 mb-1">Longitude</label>
                  <input type="number" step="any" value={form.longitude} data-testid="takmir-longitude"
                    onChange={e => setForm(f => ({ ...f, longitude: e.target.value }))}
                    className={fieldCls} placeholder="104.000000" />
                </div>
              </div>
              {form.latitude && form.longitude && !isNaN(parseFloat(form.latitude)) && (
                <a href={`https://www.google.com/maps/search/?api=1&query=${form.latitude},${form.longitude}`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:underline">
                  <MapPin className="w-3 h-3" /> Verifikasi di Google Maps
                </a>
              )}
            </div>
            <hr className="border-stone-100" />
            <div className="relative"><User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" /><input data-testid="register-name" className={inputCls} placeholder="Nama Takmir" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
            <div className="relative"><Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" /><input data-testid="register-email" type="email" className={inputCls} placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
            <div className="relative"><Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" /><input data-testid="register-password" type="password" className={inputCls} placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required /></div>
            <button type="submit" data-testid="register-submit" disabled={loading} className="w-full bg-emerald-700 hover:bg-emerald-800 disabled:bg-emerald-400 text-white font-body font-semibold py-3 rounded-full">{loading ? 'Memproses...' : 'Daftar Sekarang'}</button>
          </form>

          <div className="mt-5 text-center">
            <button onClick={() => navigate('/login')} className="text-sm font-body text-emerald-700 hover:text-emerald-800 font-medium">Sudah punya akun? Masuk</button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default TakmirRegisterPage;
